import { useEffect, useState } from "react";
import {
  Share2,
  Facebook,
  Instagram,
  Youtube,
  MessageCircle,
  X,
  Radio,
  Church,
} from "lucide-react";
import {
  LINK_RADIO_FB,
  LINK_CHURCH_FB,
  LINK_CHURCH_YT,
  LINK_CHURCH_IG,
  CONTACT_PHONES,
  whatsappHrefFor,
} from "../lib/siteConfig";

/**
 * Floating "social" FAB anchored to the bottom-left corner.
 * Opens upward on hover (desktop) or tap (mobile).
 * Facebook prompts the user to choose between Life Talk Radio and
 * Father's House Tauranga — since we maintain two FB pages.
 */
export default function SocialFab() {
  const [open, setOpen] = useState(false);
  const [fbPicker, setFbPicker] = useState(false);
  const [waPicker, setWaPicker] = useState(false);

  // (Picker is independent: it stays open until the user explicitly closes it.)

  // Lock body scroll when FB picker open (mobile UX)
  useEffect(() => {
    if (!fbPicker) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [fbPicker]);

  const items = [
    {
      key: "whatsapp",
      label: "WhatsApp",
      Icon: MessageCircle,
      onClick: () => setWaPicker(true),
      color: "#25D366",
    },
    {
      key: "instagram",
      label: "Instagram",
      Icon: Instagram,
      href: LINK_CHURCH_IG,
      color: "#E1306C",
    },
    {
      key: "youtube",
      label: "YouTube",
      Icon: Youtube,
      href: LINK_CHURCH_YT,
      color: "#FF0033",
    },
    {
      key: "facebook",
      label: "Facebook",
      Icon: Facebook,
      onClick: () => setFbPicker(true),
      color: "#1877F2",
    },
  ];

  return (
    <>
      <div
        data-testid="social-fab"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className={`fixed bottom-6 left-6 z-50 flex flex-col-reverse items-center pointer-events-none ${
          open ? "gap-3" : "gap-0"
        }`}
      >
        {/* Trigger */}
        <button
          type="button"
          data-testid="social-fab-trigger"
          aria-expanded={open}
          aria-label="Open social media menu"
          onClick={() => setOpen((v) => !v)}
          className={`pointer-events-auto relative w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 shadow-[0_10px_40px_-8px_rgba(197,0,26,0.6)] ${
            open
              ? "bg-[#0B0C10] border border-[#C5D1EB]/30 text-white rotate-90"
              : "bg-[#C5001A] hover:bg-[#E31C3D] text-white"
          }`}
        >
          {open ? <X size={20} /> : <Share2 size={20} />}
          {/* Soft ambient ring */}
          <span
            aria-hidden
            className="absolute inset-0 rounded-full bg-[#C5001A] opacity-40 blur-xl -z-10"
          />
        </button>

        {/* Items */}
        <div
          className={`flex flex-col-reverse items-center gap-3 transition-all duration-300 ${
            open
              ? "opacity-100 translate-y-0 pointer-events-auto max-h-[400px] mb-0"
              : "opacity-0 translate-y-3 pointer-events-none max-h-0 mb-0 overflow-hidden"
          }`}
          aria-hidden={!open}
        >
          {items.map((item, i) => {
            const common = {
              "data-testid": `fab-${item.key}`,
              "aria-label": item.label,
              className:
                "pointer-events-auto group relative w-12 h-12 rounded-full bg-[#1F2833]/80 backdrop-blur-md border border-white/10 text-white hover:scale-110 hover:border-white/30 transition-all duration-200 flex items-center justify-center",
              style: { transitionDelay: open ? `${i * 40}ms` : "0ms" },
            };
            const inner = (
              <>
                <item.Icon size={17} style={{ color: item.color }} />
                {/* Tooltip */}
                <span className="absolute left-full ml-3 px-2.5 py-1 text-[11px] tracking-wide uppercase text-white bg-[#0B0C10] border border-white/10 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                  {item.label}
                </span>
              </>
            );
            return item.onClick ? (
              <button type="button" onClick={item.onClick} {...common}>
                {inner}
              </button>
            ) : (
              <a
                href={item.href}
                target="_blank"
                rel="noreferrer"
                {...common}
              >
                {inner}
              </a>
            );
          })}
        </div>
      </div>

      {/* Facebook chooser modal */}
      {fbPicker && (
        <div
          data-testid="fb-picker"
          className="fixed inset-0 z-[60] flex items-center justify-center px-5"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="absolute inset-0 bg-[#0B0C10]/80 backdrop-blur-sm"
            onClick={() => setFbPicker(false)}
          />
          <div className="relative bg-[#1F2833] border border-white/10 max-w-md w-full p-7 sm:p-8 shadow-[0_20px_80px_-20px_rgba(0,0,0,0.8)]">
            <button
              data-testid="fb-picker-close"
              onClick={() => setFbPicker(false)}
              aria-label="Close"
              className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center text-[#C5D1EB] hover:text-white"
            >
              <X size={16} />
            </button>
            <Facebook size={22} className="text-[#1877F2]" />
            <h3 className="mt-3 font-display text-xl text-white tracking-tight">
              Choose a Facebook page
            </h3>
            <p className="mt-2 text-sm text-[#C5D1EB] leading-relaxed">
              We run two Facebook pages — which would you like to visit?
            </p>

            <div className="mt-6 grid gap-3">
              <a
                href={LINK_RADIO_FB}
                target="_blank"
                rel="noreferrer"
                data-testid="fb-pick-radio"
                onClick={() => setFbPicker(false)}
                className="group flex items-center gap-3 bg-[#0B0C10] border border-white/10 hover:border-[#C5001A] p-4 transition-colors"
              >
                <span className="w-10 h-10 flex items-center justify-center bg-[#1877F2]/15 text-[#1877F2]">
                  <Radio size={17} />
                </span>
                <div className="flex-1 text-left">
                  <p className="font-display text-white text-sm">Life Talk Radio</p>
                  <p className="text-[11px] text-[#C5D1EB]/70">@lifetalktga</p>
                </div>
              </a>
              <a
                href={LINK_CHURCH_FB}
                target="_blank"
                rel="noreferrer"
                data-testid="fb-pick-church"
                onClick={() => setFbPicker(false)}
                className="group flex items-center gap-3 bg-[#0B0C10] border border-white/10 hover:border-[#C5001A] p-4 transition-colors"
              >
                <span className="w-10 h-10 flex items-center justify-center bg-[#1877F2]/15 text-[#1877F2]">
                  <Church size={17} />
                </span>
                <div className="flex-1 text-left">
                  <p className="font-display text-white text-sm">
                    Father's House Tauranga
                  </p>
                  <p className="text-[11px] text-[#C5D1EB]/70">
                    Indian Church · NZ
                  </p>
                </div>
              </a>
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp number chooser — centred modal mirrors the FB picker */}
      {waPicker && (
        <div
          data-testid="wa-picker"
          className="fixed inset-0 z-[60] flex items-center justify-center px-5"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="absolute inset-0 bg-[#0B0C10]/80 backdrop-blur-sm"
            onClick={() => setWaPicker(false)}
          />
          <div className="relative bg-[#1F2833] border border-white/10 max-w-md w-full p-7 sm:p-8 shadow-[0_20px_80px_-20px_rgba(0,0,0,0.8)]">
            <button
              data-testid="wa-picker-close"
              onClick={() => setWaPicker(false)}
              aria-label="Close"
              className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center text-[#C5D1EB] hover:text-white"
            >
              <X size={16} />
            </button>
            <MessageCircle size={22} className="text-[#25D366]" />
            <h3 className="mt-3 font-display text-xl text-white tracking-tight">
              Choose a WhatsApp number
            </h3>
            <p className="mt-2 text-sm text-[#C5D1EB] leading-relaxed">
              Tap a number to open the chat directly.
            </p>

            <div className="mt-6 grid gap-3">
              {CONTACT_PHONES.map((p, i) => (
                <a
                  key={p}
                  href={whatsappHrefFor(p)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`wa-pick-${i}`}
                  onClick={() => setWaPicker(false)}
                  className="group flex items-center gap-3 bg-[#0B0C10] border border-white/10 hover:border-[#25D366] p-4 transition-colors"
                >
                  <span className="w-10 h-10 flex items-center justify-center bg-[#25D366]/15 text-[#25D366]">
                    <MessageCircle size={17} />
                  </span>
                  <div className="flex-1 text-left">
                    <p className="text-[11px] tracking-[0.25em] uppercase text-[#C5D1EB]/70">
                      Contact {i + 1}
                    </p>
                    <p className="font-display text-white text-sm">{p}</p>
                  </div>
                  <span className="text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/60 group-hover:text-white">
                    Chat →
                  </span>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
